import { Box, SxProps, Typography, useTheme } from '@mui/material';
import Divider from '@mui/material/Divider';
import Slider from '@mui/material/Slider';
import Switch from '@mui/material/Switch';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { useTranslation } from '../../services/translation/hooks/useTranslation';
import { flexCenter } from '../commonUI/cssClasses';
import { DiagnosticFeature } from '../commonUI/viewMode/DiagnosticFeature';
import {
  selectDisplayUnknownMasks,
  selectMaskInstanceColors,
  selectMaskOpacity,
  selectMaskOutline,
  setDisplayUnknownMasks,
  setMaskInstanceColors,
  setMaskOpacity,
  setMaskOutline,
} from './imageSlice';
import MaskLayerTogglesAndColors from './MaskLayerTogglesAndColors';
const MaskSettingsPopup: React.FC = () => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const maskOpacity = useAppSelector(selectMaskOpacity);
  const maskOutline = useAppSelector(selectMaskOutline);
  const displayUnknownMasks = useAppSelector(selectDisplayUnknownMasks);
  const maskInstanceColors = useAppSelector(selectMaskInstanceColors);
  const { t } = useTranslation();

  const handleDisplayUnknownMaskToggleClick = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    dispatch(setDisplayUnknownMasks(event.target.checked));
  };

  const handleOutlineToggleClick = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    dispatch(setMaskOutline(event.target.checked));
  };

  const handleOpacityChange = (event: Event, newValue: number | number[]) => {
    if (typeof newValue === 'number') {
      dispatch(setMaskOpacity(newValue));
    }
  };

  const handleMaskInstanceColorsToggleClick = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    dispatch(setMaskInstanceColors(event.target.checked));
  };

  const titleStyles: SxProps = {
    pb: '0.5rem',
    color: theme.colors.darkTheme.grey.muted,
  };

  const dividerElement = (
    <Divider
      sx={{
        width: '100%',
        bgcolor: theme.colors.darkTheme.grey.muted,
        mb: '1rem',
      }}
    />
  );

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        p: '1rem',
      }}
    >
      <Box sx={{ ...flexCenter, flexDirection: 'column' }}>
        <Typography variant='h6' sx={{ ...titleStyles }}>
          {t('layerControls', { ns: 'ranch-view' })}
        </Typography>
        {dividerElement}
        <MaskLayerTogglesAndColors />
      </Box>
      <Typography variant='h6' sx={{ ...titleStyles }}>
        {t('globalControls', { ns: 'ranch-view' })}
      </Typography>
      {dividerElement}
      <DiagnosticFeature>
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Typography
            variant='body2'
            sx={{ color: theme.colors.darkTheme.grey.muted }}
          >
            {t('showUnknownMasks', { ns: 'ranch-view' })}
          </Typography>
          <Switch
            checked={displayUnknownMasks}
            onChange={handleDisplayUnknownMaskToggleClick}
            slotProps={{ input: { 'aria-label': 'Mask Fill' } }}
            color='primary'
          />
        </Box>
      </DiagnosticFeature>
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Typography
          variant='body2'
          sx={{ color: theme.colors.darkTheme.grey.muted }}
        >
          {t('uniqueColorPerMask', { ns: 'ranch-view' })}
        </Typography>
        <Switch
          checked={maskInstanceColors}
          onChange={handleMaskInstanceColorsToggleClick}
          slotProps={{ input: { 'aria-label': 'Mask Instance Colors' } }}
          color='primary'
        />
      </Box>
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Typography
          variant='body2'
          sx={{ color: theme.colors.darkTheme.grey.muted }}
        >
          {t('outlines', { ns: 'ranch-view' })}
        </Typography>
        <Switch
          checked={maskOutline}
          onChange={handleOutlineToggleClick}
          inputProps={{ 'aria-label': 'Mask Fill' }}
          color='primary'
        />
      </Box>
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Typography
          variant='body2'
          sx={{
            color: theme.colors.darkTheme.grey.muted,
            mt: '0.5rem',
            mb: '0.5rem',
            whiteSpace: 'nowrap',
          }}
        >
          {t('fillOpacity', { ns: 'ranch-view' })}
        </Typography>
        <Slider
          value={maskOpacity}
          aria-label='Mask Opacity'
          defaultValue={maskOpacity}
          valueLabelDisplay='off'
          min={0}
          max={100}
          onChange={handleOpacityChange}
          sx={{ width: '90%' }}
          color='primary'
        />
      </Box>
    </Box>
  );
};

export default MaskSettingsPopup;
